import { useMemo } from 'react';
import { Boxes } from 'lucide-react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@ncoder/ui';
import type { OrgServiceUsageBreakdown } from '@/services/org-admin';
import { formatCurrencyUsd, labelForService } from './usageFormat';

const SKELETON_WIDTHS = ['w-36', 'w-28', 'w-44'] as const;

function SkeletonServiceUsageRow({ nameWidth }: { nameWidth: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,160px)_1fr_72px] items-center gap-3 px-1.5 py-1">
      <div className={`h-3 rounded-md bg-muted-foreground/10 loading-parallax ${nameWidth}`} />
      <div className="h-3 rounded-full bg-muted-foreground/[0.07] loading-parallax" />
      <div className="ml-auto h-3 w-12 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
    </div>
  );
}

export interface UsageByServiceChartProps {
  data: OrgServiceUsageBreakdown[];
  periodLabel: string;
  isLoading?: boolean;
}

export function UsageByServiceChart({ data: rawData, periodLabel, isLoading }: UsageByServiceChartProps) {
  const data = useMemo(() => [...rawData].sort((a, b) => b.cost_usd - a.cost_usd), [rawData]);

  return (
    <div className="studio-card p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Usage by service</h3>
          <p className="text-xs text-muted-foreground">Cost by product service — {periodLabel}.</p>
        </div>
      </div>

      <div className="mt-4 h-52 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2">
            {SKELETON_WIDTHS.map((nameWidth, index) => (
              <SkeletonServiceUsageRow key={index} nameWidth={nameWidth} />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Boxes className="h-5 w-5 text-primary" />
            </div>
            <p>No service activity in this window.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Input tokens</TableHead>
                <TableHead className="text-right">Output tokens</TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.service}>
                  <TableCell className="font-medium">{labelForService(row.service)}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.run_count.toLocaleString()}</TableCell>
                  <TableCell className={`text-right tabular-nums ${row.failed_count ? 'text-warning' : ''}`}>
                    {row.failed_count.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.input_tokens.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.output_tokens.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrencyUsd(row.cost_usd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
